import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useRememberListUrl } from '../lib/listReturn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, canDelete, can } from '../lib/auth';

interface ProductRow {
  id: string; name: string; client: string; dieNumber: string; artwork: string;
  noOfUps: string; foilingColor: string; cartonType: string;
}
interface ListResp { rows: ProductRow[]; total: number; page: number; pageSize: number }

const PAGE_SIZE = 50;

export function ProductsPage() {
  useRememberListUrl();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page') ?? '1'));
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['products', { page, q }],
    queryFn: () =>
      api<ListResp>(
        `/api/products?page=${page}&pageSize=${PAGE_SIZE}` + (q ? `&q=${encodeURIComponent(q)}` : ''),
      ),
  });

  const { user } = useAuth();
  // Purchase and CRM may read specifications but never change them; the API
  // enforces the same rule, so this only hides controls that would 403.
  const canEditProducts = can(user, 'EDIT_PRODUCT');
  const del = useMutation({
    mutationFn: (ids: string[]) =>
      api('/api/products/delete', { method: 'POST', body: JSON.stringify({ ids }) }),
    onSuccess: () => { setSelected(new Set()); qc.invalidateQueries({ queryKey: ['products'] }); },
    onError: (e: Error) => {
      const m = /^product_has_active_records:(.+):(.+)$/.exec(e.message);
      if (m) alert(`Cannot delete product ${m[1]} — it is referenced by ${m[2]}. Remove or reassign the PO/job card first.`);
      else alert(e.message);
    },
  });

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (search) p.set('q', search); else p.delete('q');
    p.set('page', '1');
    setParams(p);
  }
  function goPage(next: number) {
    const p = new URLSearchParams(params);
    p.set('page', String(next));
    setParams(p);
  }
  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-brand">Products</h1>
          <p className="text-xs text-slate-500">{total.toLocaleString()} active</p>
        </div>
        {canEditProducts && <Link to="/products/new" className="btn-primary">+ New Product</Link>}
      </div>

      <form className="flex flex-wrap items-end gap-2" onSubmit={applySearch}>
        <div className="flex-1 min-w-[240px]">
          <label className="label">Search</label>
          <input className="input" placeholder="Name, Code, Die No, Client…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button className="btn-primary" type="submit">Search</button>
        {q && <button type="button" className="btn-ghost" onClick={() => { setSearch(''); setParams({}); }}>Reset</button>}
        {canDelete(user) && selected.size > 0 && (
          <button type="button" className="btn-danger" disabled={del.isPending}
            onClick={() => { if (confirm(`Delete ${selected.size}?`)) del.mutate(Array.from(selected)); }}>
            Delete {selected.size}
          </button>
        )}
      </form>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Die No</th>
                <th className="px-3 py-2">Artwork</th>
                <th className="px-3 py-2">Ups</th>
                <th className="px-3 py-2">Foiling</th>
                <th className="px-3 py-2">Carton</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && <tr><td colSpan={10} className="p-8 text-center text-slate-500">Loading…</td></tr>}
              {list.isError && <tr><td colSpan={10} className="p-8 text-center text-red-600">Failed: {(list.error as Error).message}</td></tr>}
              {!list.isLoading && rows.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-slate-500">No records.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} /></td>
                  <td className="px-3 py-2 font-mono text-xs">{r.id}</td>
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 text-slate-600">{r.client}</td>
                  <td className="px-3 py-2 text-slate-600">{r.dieNumber}</td>
                  <td className="px-3 py-2 text-slate-600">{r.artwork}</td>
                  <td className="px-3 py-2">{r.noOfUps}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{r.foilingColor}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{r.cartonType}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-3">
                      {canEditProducts
                        ? <Link to={`/products/${r.id}/edit`} className="text-brand hover:underline text-sm">Edit</Link>
                        : <Link to={`/products/${r.id}/edit`} className="text-slate-500 hover:underline text-sm">View</Link>}
                      <Link to={`/products/${r.id}/print`} className="text-red-600 hover:underline text-sm">Card</Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">Page {page} of {totalPages.toLocaleString()}</div>
        <div className="flex gap-2">
          <button className="btn-ghost" disabled={page <= 1} onClick={() => goPage(page - 1)}>‹ Prev</button>
          <button className="btn-ghost" disabled={page >= totalPages} onClick={() => goPage(page + 1)}>Next ›</button>
        </div>
      </div>
    </div>
  );
}
